import React from 'react';
import { jsPDF } from 'jspdf';
import { toJpeg } from 'html-to-image';
import { useReactToPrint } from 'react-to-print';

export interface PDFOptions {
  filename?: string;
  quality?: number;
  backgroundColor?: string;
  margin?: {
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
  };
}

export type PrintMode = 'image' | 'text';

export const takeScreenshotAsPDF = async (
  element: HTMLElement,
  options: PDFOptions = {},
): Promise<void> => {
  try {
    const {
      filename = 'document.pdf',
      quality = 1.0,
      backgroundColor = '#ffffff',
      margin = { top: 5, right: 5, bottom: 5, left: 5 },
    } = options;

    // Find the actual content container (might be nested)
    let targetElement = element;

    // Look for common scrollable container patterns
    const scrollableSelectors = [
      '[class*="overflow-auto"]',
      '[class*="overflow-scroll"]',
      '[class*="flex-1"]',
      '[class*="content"]',
      '[class*="body"]',
    ];

    for (const selector of scrollableSelectors) {
      const found = element.querySelector(selector) as HTMLElement;
      if (
        found &&
        (found.scrollHeight > found.clientHeight ||
          found.scrollWidth > found.clientWidth)
      ) {
        targetElement = found;
        break;
      }
    }

    // Store original scroll position and overflow
    const originalScrollTop = targetElement.scrollTop;
    const originalScrollLeft = targetElement.scrollLeft;
    const originalOverflow = targetElement.style.overflow;
    const originalHeight = targetElement.style.height;
    const originalMaxHeight = targetElement.style.maxHeight;
    const originalPosition = targetElement.style.position;
    const originalWidth = targetElement.style.width;
    const originalMaxWidth = targetElement.style.maxWidth;

    // Temporarily modify element to capture full content and stretch to full width
    targetElement.style.overflow = 'visible';
    targetElement.style.height = 'auto';
    targetElement.style.maxHeight = 'none';
    targetElement.style.position = 'relative';
    targetElement.style.width = '100%';
    targetElement.style.maxWidth = 'none';
    targetElement.scrollTop = 0;
    targetElement.scrollLeft = 0;

    // Get the full scrollable dimensions
    const scrollHeight = targetElement.scrollHeight;
    const scrollWidth = targetElement.scrollWidth;

    // Convert pixels to mm (assuming 96 DPI)
    const pixelsToMm = 25.4 / 96;
    const contentWidthMm = scrollWidth * pixelsToMm;
    const contentHeightMm = scrollHeight * pixelsToMm;

    // Use a background colour that matches the user's current theme so that
    // transparent areas in the captured element don't bleed through as white
    // patches on a dark-mode dashboard.
    const isDark = document.documentElement.classList.contains('dark');
    const resolvedBackground = backgroundColor === '#ffffff' && isDark
      ? '#111827' // gray-900 — matches Tailwind's dark:bg-gray-900
      : backgroundColor;

    // Capture the content as image using html-to-image
    const dataUrl = await toJpeg(targetElement, {
      backgroundColor: resolvedBackground,
      width: scrollWidth,
      height: scrollHeight,
      quality: quality,
      pixelRatio: 2, // Higher pixel ratio for better quality
      cacheBust: true,
    });

    // Restore original element state
    targetElement.style.overflow = originalOverflow;
    targetElement.style.height = originalHeight;
    targetElement.style.maxHeight = originalMaxHeight;
    targetElement.style.position = originalPosition;
    targetElement.style.width = originalWidth;
    targetElement.style.maxWidth = originalMaxWidth;
    targetElement.scrollTop = originalScrollTop;
    targetElement.scrollLeft = originalScrollLeft;

    // Create PDF with exact content dimensions
    const totalWidth =
      contentWidthMm + (margin.left || 0) + (margin.right || 0);
    const totalHeight =
      contentHeightMm + (margin.top || 0) + (margin.bottom || 0);

    const pdf = new jsPDF({
      unit: 'mm',
      format: [totalWidth, totalHeight],
    });

    // Add the image at exact content size
    pdf.addImage(
      dataUrl,
      'JPEG',
      margin.left || 0,
      margin.top || 0,
      contentWidthMm,
      contentHeightMm,
    );

    // Save the PDF
    pdf.save(filename);
  } catch (error) {
    console.error('Error creating PDF:', error);
    throw error;
  }
};

/**
 * Hook to trigger text-based PDF printing using react-to-print
 * This creates a text-selectable, copy PDF with proper page breaks
 */
export const useTextPDFPrint = (
  contentRef: React.RefObject<HTMLElement | null>,
  options: { filename?: string } = {},
) => {
  const handlePrint = useReactToPrint({
    // TODO: Needs to be tested
    contentRef,
    documentTitle: options.filename?.replace(/\.pdf$/i, '') || 'document',
    onBeforePrint: () => {
      // Ensure the element is visible and properly styled
      if (contentRef.current) {
        // Make sure the element is visible for printing
        const originalDisplay = contentRef.current.style.display;
        const originalVisibility = contentRef.current.style.visibility;
        contentRef.current.style.display = 'block';
        contentRef.current.style.visibility = 'visible';

        return Promise.resolve().then(() => {
          // Restore original styles after capture
          if (contentRef.current) {
            contentRef.current.style.display = originalDisplay;
            contentRef.current.style.visibility = originalVisibility;
          }
        });
      }
      return Promise.resolve();
    },
    pageStyle: `
      @page {
        size: A4;
           margin: 10mm 10mm;
      }
      body {
        margin: 0;
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        * {
          page-break-inside: avoid;
        }
        h1, h2, h3 {
          page-break-after: avoid;
        }
      }
    `,
  });

  return handlePrint;
};
